import { useId, type ReactElement } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { ModpackConfig, ModpackConfigVocabularies } from '../../types/modpackConfig';
import type { FieldSpec } from './configFields';
import { localInputToSeconds, secondsToLocalInput } from './configFields';
import { cn } from '../../utils/cn';

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

interface ConfigFieldInputProps {
  spec: FieldSpec;
  value: string;
  onChange: (value: string) => void;
  vocabularies: ModpackConfigVocabularies | null;
  /** Every config, for the cluster-head picker. */
  existing?: ModpackConfig[];
  /** The row being edited, so the picker can label it "this server". */
  selfId?: number | null;
  /** Locked fields render disabled with the reason, rather than disappearing. */
  locked?: boolean;
  /** A blocking problem with this value. */
  error?: string;
  /** Something worth a second look that does not block. */
  warning?: string;
  className?: string;
}

/**
 * One field of a config form.
 *
 * Fields the signed-in user may not change are shown disabled rather than
 * hidden: seeing that an AMP instance exists and is not yours to edit is more
 * useful than the row appearing not to have one.
 */
export const ConfigFieldInput = ({
  spec, value, onChange, vocabularies, existing, selfId, locked, error, warning, className,
}: ConfigFieldInputProps) => {
  const id = useId();
  const options = spec.vocabulary ? vocabularies?.[spec.vocabulary] ?? [] : [];
  const numeric = spec.kind === 'number';
  const inputClass = cn(
    'glass-input w-full',
    (numeric || spec.kind === 'ip') && 'font-mono',
    error && 'border-red-500/50',
    !error && warning && 'border-amber-500/40',
  );

  let control: ReactElement;

  if (spec.kind === 'select') {
    control = (
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={locked} className="glass-select w-full">
        <option value="">—</option>
        {/* A row can hold a value the vocabulary no longer lists (the data has
            both "NeoForge" and "neoforge"), so keep it selectable. */}
        {!options.includes(value) && value !== '' && <option value={value}>{value}</option>}
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (spec.kind === 'combobox') {
    // Free text with suggestions: anything goes, but what is already in use is
    // one keystroke away.
    const listId = `${id}-suggestions`;
    control = (
      <>
        <input
          id={id}
          type="text"
          list={listId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={spec.placeholder}
          disabled={locked}
          className={inputClass}
          autoComplete="off"
        />
        <datalist id={listId}>
          {options.map((o) => <option key={o} value={o} />)}
        </datalist>
      </>
    );
  } else if (spec.kind === 'timestamp') {
    // The column is unix seconds; nobody should have to know that. The value
    // the form carries stays in seconds so change detection is exact.
    const seconds = Number(value);
    control = (
      <input
        id={id}
        type="datetime-local"
        value={secondsToLocalInput(Number.isFinite(seconds) ? seconds : null)}
        onChange={(e) => {
          const next = localInputToSeconds(e.target.value);
          onChange(next === null ? '' : String(next));
        }}
        disabled={locked}
        className={inputClass}
        aria-invalid={Boolean(error) || undefined}
      />
    );
  } else if (spec.kind === 'server_picker') {
    // The head is chosen from real servers, not typed as an id nobody knows.
    const servers = [...(existing ?? [])].sort((a, b) => a.server_address.localeCompare(b.server_address));
    control = (
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={locked} className="glass-select w-full font-mono">
        <option value="">
          {selfId !== null && selfId !== undefined ? 'This server (heads its own cluster)' : 'This server — heads its own cluster'}
        </option>
        {servers.map((s) => (
          <option key={s.id} value={String(s.id)}>
            {s.server_address}
            {s.cluster_name && s.cluster_name !== 'default' ? ` · ${s.cluster_name}` : ''}
            {s.id === selfId ? ' · this server' : s.cluster_parent === s.id ? ' · head' : ''}
          </option>
        ))}
      </select>
    );
  } else {
    control = (
      <input
        id={id}
        type={numeric ? 'number' : 'text'}
        inputMode={numeric ? 'numeric' : spec.kind === 'ip' ? 'decimal' : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={spec.placeholder}
        disabled={locked}
        className={inputClass}
        aria-invalid={Boolean(error) || undefined}
      />
    );
  }

  return (
    <div className={cn('min-w-0', className)}>
      <label htmlFor={id} className={LABEL_CLASS}>
        {spec.label}
        {spec.adminOnly && <span className="badge badge-amber ml-2">Admin</span>}
      </label>

      {control}

      {error ? (
        <p className="text-xs text-red-300 mt-1 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{error}</span>
        </p>
      ) : warning ? (
        <p className="text-xs text-amber-300 mt-1 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{warning}</span>
        </p>
      ) : (spec.help || locked) ? (
        <p className="text-xs text-glass-500 mt-1">
          {locked
            ? 'Requires argus.modpackconfigs.admin — this decides how the server is reached and restarted.'
            : spec.help}
        </p>
      ) : null}

      {spec.kind === 'timestamp' && (
        <p className="text-xs text-glass-500 mt-1 tabular-nums">
          {value && Number(value) > 0
            ? new Date(Number(value) * 1000).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
            : spec.emptyLabel ?? 'Not set'}
        </p>
      )}
    </div>
  );
};
